// Ajustes > Abas > My Planner > Limpar a agenda: apaga tarefas em massa (hoje, mês, período, por nome ou tudo),
// com a lista do que vai sair, confirmação e a senha da conta antes de apagar.
// 0.67 (texto 05 F): depois do filtro aparece "20 tarefas · Ver lista"; a lista vem toda marcada e dá para desmarcar;
// a tarefa que se repete fica numa linha só ("🔁 20 vezes") e abre os dias. Tudo o que sai vai para a Lixeira.
import { useEffect, useMemo, useState } from 'react';
import { Alert, FlatList, Pressable, ScrollView, Text, View } from 'react-native';
import { Btn, Chip, Field, Sheet } from '../../components/ui';
import { PickerModal } from '../../components/Pickers';
import { C, alpha } from '../../components/theme';
import { useDoc } from '../../store/store';
import { aplicarLimpeza, planoLimpeza, type Limpeza, type PlanoLimpeza } from '../../store/planner';
import type { Task } from '../../store/types';
import { addDays, brYmd, diaCurto, horaTela, parseYmd, today, ymd } from '../../lib/date';
import { occursOn } from '../../lib/recur';
import { paraLixeira } from '../../lib/lixeira';
import { useAuth } from '../../auth/AuthContext';
import { t } from '../../lib/i18n';
import { BotaoAcao, Bloco, Dica } from '../../components/ajustes/Linhas';
import { ROLAGEM_TECLADO } from '../../components/teclado';

type Modo = 'hoje' | 'mes' | 'periodo' | 'nome' | 'tudo';
type ModoNome = 'periodo' | 'antes' | 'historico';
type Vista = 'filtro' | 'lista' | 'dias' | 'senha';
/** Uma linha da lista: a tarefa inteira (inteira) ou só alguns dias dela; dias = as vezes que dá para escolher (null = a tarefa toda, sem escolher dias). */
type LinhaLimpeza = { tk: Task; inteira: boolean; dias: string[] | null };

const LIMITE_DIAS = 4000;
const repete = (tk: Task) => tk.recur.type !== 'none' || !!tk.end;

function BotaoData({ rotulo, valor, cor, onPress }: { rotulo: string; valor: string; cor: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={{ flex: 1, padding: 12, borderRadius: 12, backgroundColor: C.card2, borderWidth: 1, borderColor: alpha(cor, 0.4), gap: 2 }}>
      <Text style={{ color: C.sub, fontSize: 11 }}>{rotulo}</Text>
      <Text style={{ color: C.text, fontWeight: '700' }}>{brYmd(valor)}</Text>
    </Pressable>
  );
}

function Caixa({ on, cor, meio, onPress }: { on: boolean; cor: string; meio?: boolean; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} hitSlop={10} style={{ width: 24, height: 24, borderRadius: 6, borderWidth: 2, borderColor: on || meio ? cor : C.mute,
      backgroundColor: on ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
      {on ? <Text style={{ color: '#fff', fontWeight: '900', fontSize: 13 }}>✓</Text> : meio ? <View style={{ width: 10, height: 2, backgroundColor: cor }} /> : null}
    </Pressable>
  );
}

/** As linhas da lista, a partir do plano de limpeza da store (que já sabe o que sai inteiro e que dias saem). */
function linhasDo(plano: PlanoLimpeza, tasks: Task[]): LinhaLimpeza[] {
  const porId = new Map(tasks.map(x => [x.id, x]));
  const r: LinhaLimpeza[] = [];
  // Tarefa que se repete sem data de fim (F, passo 4): mostra as vezes até a última data a que chegam as outras
  // tarefas da lista, e no mínimo os próximos 12 meses. Todas marcadas = apaga a série inteira.
  const ultimas = plano.apagar.map(id => porId.get(id)).filter((x): x is Task => !!x && !(x.recur.type !== 'none' && !x.end))
    .map(x => x.end ?? x.start);
  const horizonte = [addDays(today(), 365), ...ultimas].sort().pop()!;
  for (const id of plano.apagar) {
    const tk = porId.get(id);
    if (!tk) continue;
    let dias: string[] | null = null;
    if (repete(tk)) {   // repete até uma data (ou sem fim, até o horizonte): dá para escolher as vezes
      dias = [];
      const fim = tk.end ?? horizonte;
      for (let d = tk.start, i = 0; d <= fim && i < LIMITE_DIAS; i++, d = addDays(d, 1)) if (occursOn(tk, d)) dias.push(d);
      if (dias.length <= 1 && tk.end) dias = null;
      else if (!dias.length) dias = null;
    }
    r.push({ tk, inteira: true, dias });
  }
  for (const [id, ds] of Object.entries(plano.pular)) { const tk = porId.get(id); if (tk) r.push({ tk, inteira: false, dias: ds }); }
  return r.sort((a, b) => (a.tk.start + (a.tk.time ?? '')).localeCompare(b.tk.start + (b.tk.time ?? '')));
}

export function LimparAgenda({ cor }: { cor: string }) {
  const tasks = useDoc('planner').tasks;
  const { conferirSenha } = useAuth();
  const hoje = today();
  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState<Modo>('hoje');
  const [modoNome, setModoNome] = useState<ModoNome>('periodo');
  const [nome, setNome] = useState('');
  const [de, setDe] = useState(hoje.slice(0, 8) + '01');
  const [ate, setAte] = useState(hoje);
  const [antes, setAntes] = useState(hoje);
  const [picker, setPicker] = useState<'de' | 'ate' | 'antes' | null>(null);
  const [vista, setVista] = useState<Vista>('filtro');
  const [diasDe, setDiasDe] = useState<string | null>(null);
  const [off, setOff] = useState<Set<string>>(new Set());               // tarefas desmarcadas
  const [diasOff, setDiasOff] = useState<Record<string, string[]>>({}); // dias desmarcados de cada tarefa
  const [senha, setSenha] = useState('');
  const [indo, setIndo] = useState(false);

  const filtro: Limpeza | null = useMemo(() => {
    const fimMes = ymd(new Date(parseYmd(hoje).getFullYear(), parseYmd(hoje).getMonth() + 1, 0));
    switch (modo) {
      case 'hoje': return { de: hoje, ate: hoje };
      case 'mes': return { de: hoje.slice(0, 8) + '01', ate: fimMes };
      case 'periodo': return de <= ate ? { de, ate } : null;
      case 'tudo': return { de: null, ate: null };
      case 'nome':
        if (!nome.trim()) return null;
        if (modoNome === 'historico') return { de: null, ate: null, nome };
        if (modoNome === 'antes') return { de: null, ate: addDays(antes, -1), nome };
        return de <= ate ? { de, ate, nome } : null;
    }
  }, [modo, modoNome, nome, de, ate, antes, hoje]);
  const plano = useMemo(() => (filtro ? planoLimpeza(filtro, tasks) : null), [filtro, tasks]);
  const linhas = useMemo(() => (plano ? linhasDo(plano, tasks) : []), [plano, tasks]);
  // Filtro novo: tudo marcado de novo.
  useEffect(() => { setOff(new Set()); setDiasOff({}); }, [filtro]);

  const nDias = (l: LinhaLimpeza) => (off.has(l.tk.id) ? 0 : l.dias ? l.dias.length - (diasOff[l.tk.id]?.length ?? 0) : 1);
  const cheia = (l: LinhaLimpeza) => !off.has(l.tk.id) && !(diasOff[l.tk.id]?.length);
  const marcadas = linhas.filter(l => nDias(l) > 0).length;
  const todas = linhas.length > 0 && linhas.every(cheia);

  /** O que vai sair de fato, com o que ficou marcado. */
  const escolhido: PlanoLimpeza = useMemo(() => {
    const r: PlanoLimpeza = { apagar: [], pular: {}, tarefas: 0, dias: 0 };
    for (const l of linhas) {
      if (off.has(l.tk.id)) continue;
      if (!l.dias) { r.apagar.push(l.tk.id); r.tarefas++; continue; }
      const fora = new Set(diasOff[l.tk.id] ?? []);
      const sel = l.dias.filter(d => !fora.has(d));
      if (!sel.length) continue;
      if (l.inteira && sel.length === l.dias.length) { r.apagar.push(l.tk.id); r.tarefas++; }
      else { r.pular[l.tk.id] = sel; r.dias += sel.length; }
    }
    return r;
  }, [linhas, off, diasOff]);

  const nada = !plano || !linhas.length;
  const aviso = !plano ? (modo === 'nome' && !nome.trim() ? t('Digite o nome da tarefa.') : t('A data inicial precisa ser antes da final.')) : t('Nada para apagar com esse filtro.');
  const resumo = [escolhido.tarefas ? t('{n} tarefa(s) apagada(s) por inteiro', { n: escolhido.tarefas }) : '',
    escolhido.dias ? t('{n} dia(s) tirado(s) de tarefas que se repetem', { n: escolhido.dias }) : ''].filter(Boolean).join(' · ');
  const rotuloApagar = todas ? t('Apagar todas ({n})', { n: linhas.length }) : t('Apagar {n} selecionada(s)', { n: marcadas });

  const fechar = () => { setAberto(false); setVista('filtro'); setSenha(''); setDiasDe(null); };
  /** Marca ou desmarca a tarefa inteira (com todos os dias dela). */
  const trocar = (id: string) => {
    const estavaFora = off.has(id);
    setOff(s => { const n = new Set(s); if (estavaFora) n.delete(id); else n.add(id); return n; });
    setDiasOff(m => { const n = { ...m }; delete n[id]; return n; });
  };
  const apagar = async () => {
    if (indo || (!escolhido.apagar.length && !Object.keys(escolhido.pular).length)) return;
    setIndo(true);
    try {
      if (!(await conferirSenha(senha))) { Alert.alert(t('Senha incorreta.'), t('Confira a senha da sua conta e tente de novo.')); return; }
      const porId = new Map(tasks.map(x => [x.id, x]));
      const txt = resumo;
      // as exclusões postas na fila da agenda do Google vão junto para a Lixeira (canceladas ao restaurar ou desfazer)
      const gcal = aplicarLimpeza(escolhido);
      const ex = (id: string) => (gcal[id]?.length ? { gcal: gcal[id] } : undefined);
      // Tudo vai para a Lixeira: a tarefa inteira (volta como estava) ou só os dias tirados (voltam a aparecer).
      for (const id of escolhido.apagar) { const tk = porId.get(id); if (tk) paraLixeira('planner', 'tarefa', tk.title, tk, ex(id)); }
      for (const [id, dias] of Object.entries(escolhido.pular)) {
        const tk = porId.get(id);
        if (tk) paraLixeira('planner', 'ocorrencias', t('{nome} · {n} dia(s)', { nome: tk.title, n: dias.length }), { id, dias }, ex(id));
      }
      fechar();
      Alert.alert(t('Agenda limpa'), txt + '\n\n' + t('O que saiu fica na Lixeira por 30 dias.'));
    } finally { setIndo(false); }
  };

  const valorPicker = picker === 'de' ? de : picker === 'ate' ? ate : antes;
  const periodo = (
    <View style={{ flexDirection: 'row', gap: 8 }}>
      <BotaoData rotulo={t('De')} valor={de} cor={cor} onPress={() => setPicker('de')} />
      <BotaoData rotulo={t('Até')} valor={ate} cor={cor} onPress={() => setPicker('ate')} />
    </View>
  );
  const botaoApagar = <Btn cor={C.danger} disabled={!marcadas} label={rotuloApagar} onPress={() => setVista('senha')} />;
  const todasNenhuma = (on: () => void, nenhuma: () => void) => (
    <View style={{ flexDirection: 'row', gap: 18, paddingVertical: 4 }}>
      <Pressable onPress={on} hitSlop={6}><Text style={{ color: cor, fontWeight: '700' }}>{t('Selecionar todas')}</Text></Pressable>
      <Pressable onPress={nenhuma} hitSlop={6}><Text style={{ color: cor, fontWeight: '700' }}>{t('Nenhuma')}</Text></Pressable>
    </View>
  );
  const linhaDias = linhas.find(l => l.tk.id === diasDe) ?? null;

  return (<>
    <Bloco titulo={t('Limpar a agenda')}>
      <Dica>{t('Apague de uma vez as tarefas de hoje, do mês, de um período ou pelo nome. Antes de apagar, o app mostra o que vai sair e pede a sua senha.')}</Dica>
      <BotaoAcao cor={C.danger} label={'🧹 ' + t('Limpar a agenda')} onPress={() => setAberto(true)} />
    </Bloco>

    <Sheet visible={aberto} onClose={fechar} title={t('Limpar a agenda')}>
      {vista === 'filtro' && (
        <ScrollView {...ROLAGEM_TECLADO} contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
          <Text style={{ color: C.text, fontWeight: '700' }}>{t('O que apagar')}</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            <Chip cor={cor} on={modo === 'hoje'} label={t('Hoje')} onPress={() => setModo('hoje')} />
            <Chip cor={cor} on={modo === 'mes'} label={t('Mês atual')} onPress={() => setModo('mes')} />
            <Chip cor={cor} on={modo === 'periodo'} label={t('Período')} onPress={() => setModo('periodo')} />
            <Chip cor={cor} on={modo === 'nome'} label={t('Por nome')} onPress={() => setModo('nome')} />
            <Chip cor={cor} on={modo === 'tudo'} label={t('Toda a agenda')} onPress={() => setModo('tudo')} />
          </View>
          {modo === 'periodo' && periodo}
          {modo === 'nome' && (<>
            <Field value={nome} onChangeText={setNome} placeholder={t('Nome da tarefa (ex.: Academia)')} autoCorrect={false} />
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              <Chip small cor={cor} on={modoNome === 'periodo'} label={t('Num período')} onPress={() => setModoNome('periodo')} />
              <Chip small cor={cor} on={modoNome === 'antes'} label={t('Antes de uma data')} onPress={() => setModoNome('antes')} />
              <Chip small cor={cor} on={modoNome === 'historico'} label={t('Todo o histórico')} onPress={() => setModoNome('historico')} />
            </View>
            {modoNome === 'periodo' && periodo}
            {modoNome === 'antes' && <BotaoData rotulo={t('Apagar tudo antes de')} valor={antes} cor={cor} onPress={() => setPicker('antes')} />}
          </>)}
          {modo === 'tudo' && <Text style={{ color: C.danger, fontSize: 12 }}>{t('Apaga todas as tarefas da agenda, passadas e futuras.')}</Text>}
          {nada ? (
            <View style={{ backgroundColor: C.card, borderRadius: 12, padding: 12 }}><Text style={{ color: C.sub }}>{aviso}</Text></View>
          ) : (
            <Pressable onPress={() => setVista('lista')} style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: pressed ? C.card2 : C.card, borderRadius: 12, padding: 12 })}>
              <Text style={{ color: C.text, flex: 1, fontWeight: '700' }}>{t('{n} tarefa(s)', { n: linhas.length })}{!todas ? ` · ${t('{n} marcada(s)', { n: marcadas })}` : ''}</Text>
              <Text style={{ color: cor, fontWeight: '700' }}>{t('Ver lista')} ›</Text>
            </Pressable>
          )}
          <Text style={{ color: C.mute, fontSize: 11 }}>{t('Tarefas que se repetem e passam do período continuam nos outros dias; só os dias escolhidos saem.')}</Text>
          {!nada && botaoApagar}
        </ScrollView>
      )}

      {vista === 'lista' && (
        <View style={{ flex: 1, paddingHorizontal: 16 }}>
          <Pressable onPress={() => setVista('filtro')} hitSlop={6} style={{ paddingVertical: 6 }}><Text style={{ color: C.sub }}>‹ {t('Voltar ao filtro')}</Text></Pressable>
          {todasNenhuma(() => { setOff(new Set()); setDiasOff({}); }, () => { setOff(new Set(linhas.map(l => l.tk.id))); setDiasOff({}); })}
          <FlatList data={linhas} keyExtractor={l => l.tk.id} style={{ flex: 1 }} contentContainerStyle={{ gap: 6, paddingVertical: 8 }}
            renderItem={({ item: l }) => {
              const n = nDias(l);
              const info = l.dias ? `🔁 ${n === l.dias.length ? t('{n} vezes', { n: l.dias.length }) : t('{n} de {total} vezes', { n, total: l.dias.length })}`
                : repete(l.tk) ? `🔁 ${t('todas as vezes')}` : `${brYmd(l.tk.start)}${l.tk.time ? ' ' + horaTela(l.tk.time) : ''}`;
              return (
                <Pressable onPress={() => { if (l.dias) { setDiasDe(l.tk.id); setVista('dias'); } else trocar(l.tk.id); }}
                  style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, backgroundColor: pressed ? C.card2 : C.card })}>
                  <Caixa on={cheia(l)} meio={n > 0 && !cheia(l)} cor={cor} onPress={() => trocar(l.tk.id)} />
                  <Text style={{ color: C.text, flex: 1, fontWeight: '600' }} numberOfLines={1}>{l.tk.emoji} {l.tk.title}</Text>
                  <Text style={{ color: C.sub, fontSize: 12 }}>{info}</Text>
                  {!!l.dias && <Text style={{ color: C.mute, fontSize: 18 }}>›</Text>}
                </Pressable>
              );
            }} />
          <View style={{ paddingVertical: 12, paddingBottom: 30 }}>{botaoApagar}</View>
        </View>
      )}

      {vista === 'dias' && linhaDias?.dias && (
        <View style={{ flex: 1, paddingHorizontal: 16 }}>
          <Pressable onPress={() => setVista('lista')} hitSlop={6} style={{ paddingVertical: 6 }}><Text style={{ color: C.sub }}>‹ {t('Voltar à lista')}</Text></Pressable>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }} numberOfLines={2}>{linhaDias.tk.emoji} {linhaDias.tk.title}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Marque as vezes que vão ser apagadas.')}</Text>
          {linhaDias.inteira && !linhaDias.tk.end && linhaDias.tk.recur.type !== 'none' && (
            <Text style={{ color: C.mute, fontSize: 11 }}>{t('Ela se repete sem data de fim: aparecem as vezes até {data}. Com todas marcadas, sai a tarefa inteira.', { data: brYmd(linhaDias.dias[linhaDias.dias.length - 1]) })}</Text>
          )}
          {todasNenhuma(() => { const id = linhaDias.tk.id; setOff(s => { const n = new Set(s); n.delete(id); return n; }); setDiasOff(m => ({ ...m, [id]: [] })); },
            () => { const id = linhaDias.tk.id; setOff(s => new Set(s).add(id)); setDiasOff(m => ({ ...m, [id]: [] })); })}
          <FlatList data={linhaDias.dias} keyExtractor={d => d} style={{ flex: 1 }} contentContainerStyle={{ gap: 4, paddingVertical: 8 }}
            renderItem={({ item: d }) => {
              const id = linhaDias.tk.id;
              const on = !off.has(id) && !(diasOff[id] ?? []).includes(d);
              const mudar = () => {
                if (off.has(id)) {   // estava toda desmarcada: marca só este dia
                  setOff(s => { const n = new Set(s); n.delete(id); return n; });
                  setDiasOff(m => ({ ...m, [id]: linhaDias.dias!.filter(x => x !== d) }));
                } else setDiasOff(m => { const l = m[id] ?? []; return { ...m, [id]: l.includes(d) ? l.filter(x => x !== d) : [...l, d] }; });
              };
              return (
                <Pressable onPress={mudar} style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 12, backgroundColor: pressed ? C.card2 : on ? alpha(cor, 0.1) : 'transparent' })}>
                  <Caixa on={on} cor={cor} onPress={mudar} />
                  <Text style={{ color: C.text, flex: 1 }}>{diaCurto(parseYmd(d).getDay())} · {brYmd(d)}{linhaDias.tk.time ? ' · ' + horaTela(linhaDias.tk.time) : ''}</Text>
                </Pressable>
              );
            }} />
          <View style={{ paddingVertical: 12, paddingBottom: 30 }}><Btn cor={cor} label={t('Pronto')} onPress={() => setVista('lista')} /></View>
        </View>
      )}

      {vista === 'senha' && (
        <ScrollView {...ROLAGEM_TECLADO} contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }} keyboardShouldPersistTaps="handled">
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 16 }}>⚠️ {t('Tem certeza?')}</Text>
          <Text style={{ color: C.text }}>{resumo}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('O que sair fica na Lixeira por 30 dias. Digite a senha da sua conta para confirmar.')}</Text>
          <Field value={senha} onChangeText={setSenha} placeholder={t('Sua senha')} secureTextEntry autoCapitalize="none" autoCorrect={false} />
          <Btn cor={C.danger} disabled={!senha || indo} label={indo ? t('Conferindo...') : rotuloApagar} onPress={apagar} />
          <Btn outline cor={cor} label={t('Voltar')} onPress={() => { setVista('lista'); setSenha(''); }} />
        </ScrollView>
      )}

      {picker && <PickerModal mode="date" cor={cor} value={parseYmd(valorPicker)} onClose={() => setPicker(null)}
        onPick={x => { const v = ymd(x); if (picker === 'de') setDe(v); else if (picker === 'ate') setAte(v); else setAntes(v); setPicker(null); }} />}
    </Sheet>
  </>);
}
